'use client';

import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { LandingHeaderContentWrap } from './Header.styles';
import { Topbar } from '../top-bar/Topbar';
import {
	Z_INDEX_HEADER_TEXT,
	Z_INDEX_WEATHER_WIDGET,
} from '../../../styles/zIndex';
import { ContentWrap, Css, Responsive } from '../../../ui';
import Subheading from '../headings/Subheading';
import HeaderImage from './HeaderImage';
import { IPageEntry } from '../../../contentful/types/IPage';
import defaultHeaderImage from '../../../assets/default-landing-header-image.webp';
import { AirportCodes } from '../../../types/airports';

const WeatherWidget = dynamic(
	() => import('../../../features/weather/ui/WeatherWidget'),
	{
		ssr: false,
	},
);

export interface ILandingHeaderProps {
	page: IPageEntry;
	currentAirportCode?: AirportCodes;
}

export default function LandingHeader({
	page,
	currentAirportCode,
}: ILandingHeaderProps) {
	const isSmallScreen = Responsive.useMatchesBreakpoint('tablet');
	const { headerImage, title, parent, id } = page.fields;
	const headerImageUrl = headerImage?.fields.file.url;
	const headerImageAlt = headerImage?.fields.title || 'Header image';

	const renderWeatherWidget = () =>
		currentAirportCode && <WeatherWidget airportCode={currentAirportCode} />;

	return (
		<LandingHeaderContentWrap>
			<Topbar siteSectionId={parent?.fields?.id || id} />
			<HeaderContent id="main">
				<HeaderContentLeft>
					<PageTitle>
						<Subheading type="h2">Welcome to</Subheading>
						{title}
					</PageTitle>
					{!isSmallScreen && renderWeatherWidget()}
				</HeaderContentLeft>
				<HeaderImage
					src={headerImageUrl ?? defaultHeaderImage}
					variant="landingPage"
					imageAlt={headerImageAlt}
				/>
				{isSmallScreen && renderWeatherWidget()}
			</HeaderContent>
		</LandingHeaderContentWrap>
	);
}

const HeaderContent = styled(ContentWrap)`
	margin-top: ${Css.pxToRem(64)};
	z-index: ${Z_INDEX_HEADER_TEXT};
	display: flex;
	justify-content: space-between;
	width: 100%;
	gap: ${Css.pxToRem(94)};

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		margin-top: ${Css.pxToRem(64)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		margin-top: ${Css.pxToRem(40)};
		flex-direction: column;
		gap: ${Css.pxToRem(40)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-top: ${Css.pxToRem(24)};
		gap: ${Css.pxToRem(18)};
	}

	h1 {
		max-width: 90%;

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			max-width: 80%;
		}

		// For smaller screens (ex. Samsung Galaxy Fold)
		@media (max-width: 360px) {
			max-width: 85%;
		}
	}

	.weather-widget {
		z-index: ${Z_INDEX_WEATHER_WIDGET};
		margin-bottom: ${Css.pxToRem(86)};

		@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
			margin-bottom: ${Css.pxToRem(130)};
		}

		@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
			margin-top: ${Css.pxToRem(20)};
			margin-bottom: 0;
		}

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			margin: ${Css.pxToRem(-52)} ${Css.pxToRem(32)} 0 0;
		}
	}
`;

const HeaderContentLeft = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
`;

const PageTitle = styled.h1`
	display: flex;
	flex-direction: column;
	font-size: ${Css.pxToRem(96)};
	font-weight: 700;
	line-height: 100%;
	margin-bottom: 0;

	.subheading {
		line-height: normal;
		margin-bottom: 0.3em;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		font-size: ${Css.pxToRem(80)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(32)};
		font-weight: 500;
		line-height: normal;

		.subheading {
			font-size: ${Css.pxToRem(19)};
		}
	}
`;
